/* eslint-disable @typescript-eslint/no-explicit-any */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { DcrType, EntityType } from '@/core/graphql/generated/types';

import { getReviewEntityTitle } from '../../_utils/getReviewEntityTitle';
import { generateReviewEntityRows } from '../_utils/generateReviewEntityRows';
import { reviewDiffStyles } from '../ReviewModalContent.styles';
import { ReviewRow } from '../ReviewRow/ReviewRow';

import { reviewArrayFieldStyles as sx } from './ReviewArrayField.styles';
import { ReviewDeletedItem } from './ReviewDeletedItem/ReviewDeletedItem';

const FORMATTED_LIST_KEYS = ['addresses', 'parenthcos', 'licenses'];

const getGroupHeading = (
	t: TFunction,
	fieldKey: string,
	fieldTranslations: Record<string, string> | undefined,
	count: number,
	entityType: EntityType
): string => {
	switch (fieldKey) {
		case 'addresses':
			return t('Address', { count });
		case 'parenthcos':
			return entityType === EntityType.Hco
				? t('Parent HCO', { count })
				: t('Affiliation', { count });
		case 'licenses':
			return t('License', { count });
		case 'degrees':
			return t('Medical Degree', { count });
		case 'hcpSpecialties':
			return t('Global Specialty', { count });
		case 'allSpecLocal_VId':
			return t('Local Specialties', { count });
		default:
			return fieldTranslations?.[fieldKey] ?? t(fieldKey);
	}
};

type Props = {
	fieldKey: string;
	changedItemsMap: Record<string, any> | any[];
	defaultValues: any;
	currentValues: any;
	fieldTranslations: Record<string, string> | undefined;
	dcrType: DcrType;
	entityType: EntityType;
};

export const ReviewArrayField = ({
	fieldKey,
	changedItemsMap,
	defaultValues,
	currentValues,
	fieldTranslations,
	dcrType,
	entityType
}: Props) => {
	const { t } = useTranslation();

	// Handle both Object (from getDirtyValues) and Array (fallback)
	const entries = Array.isArray(changedItemsMap)
		? changedItemsMap.map((item, idx) => [String(idx), item])
		: Object.entries(changedItemsMap);

	const dirtyEntries = entries.filter(([, changes]) => !!changes);

	if (dirtyEntries.length === 0) return null;

	const oldArray = defaultValues?.[fieldKey] || [];
	const newArray = currentValues?.[fieldKey] || [];
	const isFormattedList = FORMATTED_LIST_KEYS.includes(fieldKey);

	const getChip = (item: any): string | undefined =>
		fieldKey === 'parenthcos'
			? item?.typeOfRelationship_VId || undefined
			: undefined;

	const renderedBlocks = dirtyEntries.map(([indexStr, changes]) => {
		const index = Number(indexStr);
		const oldItem = oldArray[index];
		const newItem = newArray[index];

		// Deletion
		if (oldItem && !newItem) {
			const name = getReviewEntityTitle(fieldKey, null, oldItem);

			if (isFormattedList) {
				return (
					<ReviewRow
						key={index}
						labelKey=''
						oldVal={name}
						newVal={undefined}
						fieldTranslations={fieldTranslations}
						dcrType={dcrType}
						chip={getChip(oldItem)}
					/>
				);
			}

			return (
				<Box key={index}>
					<ReviewDeletedItem label={name} />
				</Box>
			);
		}

		// Update / Add
		if (newItem) {
			// Note(Filip): If this item was simply moved (index changed due to a deletion above it),
			const newItemId = newItem.id || newItem.vid_V;
			if (newItemId) {
				const originalVersion = oldArray.find(
					(o: { id: string; vid_V: string }) =>
						(o?.id || o?.vid_V) === newItemId
				);

				if (originalVersion) {
					// Note(Filip): If we there is an original version of this item and it looks identical to the new one (ignoring internal keys)
					// Then this is just a shift, not a user edit -> We HIDE IT.
					const isShiftOnly =
						JSON.stringify(originalVersion) === JSON.stringify(newItem);
					if (isShiftOnly) return null;
				}
			}

			if (isFormattedList) {
				return (
					<ReviewRow
						key={index}
						labelKey=''
						oldVal={
							oldItem
								? getReviewEntityTitle(fieldKey, null, oldItem)
								: undefined
						}
						newVal={getReviewEntityTitle(fieldKey, newItem, null)}
						fieldTranslations={fieldTranslations}
						dcrType={dcrType}
						chip={getChip(newItem)}
					/>
				);
			}

			const validRows = generateReviewEntityRows(
				changes,
				oldItem,
				newItem,
				fieldTranslations,
				dcrType
			);

			if (validRows.length === 0) {
				return null;
			}

			return (
				<Box key={index}>
					<Box sx={sx.validRowsContainer}>{validRows}</Box>
				</Box>
			);
		}

		return null;
	});

	const validBlocks = renderedBlocks.filter(Boolean);

	if (validBlocks.length === 0) return null;

	return (
		<Box sx={sx.nestedGroup}>
			<Typography sx={reviewDiffStyles.changeLabel}>
				{getGroupHeading(
					t,
					fieldKey,
					fieldTranslations,
					validBlocks.length,
					entityType
				)}
			</Typography>
			<Box sx={sx.flatListWrapper}>{validBlocks}</Box>
		</Box>
	);
};
